/**
 * Speech resampled piece by piece as it streams in: the examiner's voice
 * arrives at 24 kHz (backend speech/stream) and Simli takes 16 kHz. Whole
 * lines were resampled by an OfflineAudioContext (pcm.js, toPcm16), which
 * needs the whole line; this works on each piece and carries its state
 * across them, so a line has no clicks where the pieces meet.
 *
 * A low-pass filter first (a 31-tap windowed sinc, cut just under the new
 * rate's limit), so frequencies the lower rate cannot hold don't fold back
 * as hiss, then linear interpolation at the new spacing.
 */
const TAPS = 31;

function lowPass(fromRate, toRate) {
  const fc = (0.45 * toRate) / fromRate; // cycles per input sample
  const h = new Float32Array(TAPS);
  let sum = 0;
  for (let i = 0; i < TAPS; i += 1) {
    const m = i - (TAPS - 1) / 2;
    const sinc = m === 0 ? 2 * fc : Math.sin(2 * Math.PI * fc * m) / (Math.PI * m);
    h[i] = sinc * (0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (TAPS - 1)));
    sum += h[i];
  }
  return h.map((x) => x / sum);
}

/** A function taking each Int16Array piece at fromRate and returning the
 * matching Int16Array at toRate. */
export function makeResampler(fromRate, toRate) {
  if (fromRate === toRate) return (samples) => samples;
  const step = fromRate / toRate;
  const h = lowPass(fromRate, toRate);
  let history = new Float32Array(TAPS - 1);
  let pos = 0; // where the next output falls, in this piece's filtered samples
  let prev = 0; // the last filtered sample of the piece before

  return (samples) => {
    const n = samples.length;
    if (!n) return new Int16Array(0);
    const buf = new Float32Array(history.length + n);
    buf.set(history, 0);
    for (let i = 0; i < n; i += 1) buf[history.length + i] = samples[i];
    const filtered = new Float32Array(n);
    for (let j = 0; j < n; j += 1) {
      let acc = 0;
      for (let k = 0; k < TAPS; k += 1) acc += h[k] * buf[j + k];
      filtered[j] = acc;
    }
    history = buf.slice(buf.length - (TAPS - 1));

    const at = (j) => (j < 0 ? prev : filtered[j]);
    const out = [];
    while (pos < n - 1) {
      const j = Math.floor(pos);
      const f = pos - j;
      out.push(at(j) * (1 - f) + at(j + 1) * f);
      pos += step;
    }
    pos -= n;
    prev = filtered[n - 1];
    return Int16Array.from(out, (x) => Math.max(-32768, Math.min(32767, Math.round(x))));
  };
}
